import { HTTP_REQUEST_METHODS } from 'constants/http';
import { VENDOR_COMMANDS } from 'constants/vendor-commands';
import { camelCase } from 'lodash';
import { VendorCommandValues } from 'services/propel/types';
import { axiosWithRetry } from 'utils/axios';
import { DataDogLogger } from 'utils/dataDog';
import { CUSTOM_ACTION_NAMES } from 'utils/dataDog/constants';
import {
  CancelEscalationRequestBody,
  ContactFollowUpNameRequestBody,
  ContactFollowUpNameShowRequestBody,
  ContactFollowUpQuestionRequestBody,
  ContactFollowUpVerificationRequestBody,
  ContactFollowUpVerificationResendRequestBody,
  CustomerChatFeedbackRequestBody,
  CustomerChatRatingRequestBody,
  CustomerJourneyRequestBody,
  EndAdvocateChatRequestBody,
  SendAiMessageRequestBody,
} from './types/commands.types';

const baseUrl = `${process.env.CHAT_BACKEND_BASE_URL}/conversation`;
const getHeaders = (accessToken: string) => {
  return { Authorization: `Bearer ${accessToken}` };
};

/**
 * Temporary method to convert old vendor command payloads (snake_case) to the new Chat BE payload (camelCase)
 * @param oldProps
 * @returns
 */
const mapProperties = <NewProps>(oldProps: object) => {
  const res: { [k: string]: string } = {};
  Object.entries(oldProps).map(([key, value]) => {
    res[camelCase(key)] = value;
  });

  return res as NewProps;
};

export const executeCommand = <RequestBodyType extends object>(
  command: VendorCommandValues,
  requestBody: RequestBodyType,
  accessToken: string,
) => {
  DataDogLogger.action(CUSTOM_ACTION_NAMES.CHAT_BE_COMMAND_INVOKED, {
    command,
    mappedData: mapProperties(requestBody),
  });

  const commands: { [k: string]: { route: string; payload: any } } = {
    [VENDOR_COMMANDS.CARVANA_CANCEL_ESCALATION]: {
      route: 'cancel-escalation',
      payload: mapProperties<CancelEscalationRequestBody>(requestBody),
    },
    [VENDOR_COMMANDS.CARVANA_END_CHAT]: {
      route: 'end-advocate-chat',
      payload: mapProperties<EndAdvocateChatRequestBody>(requestBody),
    },
    [VENDOR_COMMANDS.CARVANA_CUSTOMER_JOURNEY]: {
      route: 'customer-journey',
      payload: mapProperties<CustomerJourneyRequestBody>(requestBody),
    },
    [VENDOR_COMMANDS.CARVANA_FEEDBACK_REQUEST_FORM]: {
      route: 'customer-chat-feedback',
      payload: mapProperties<CustomerChatFeedbackRequestBody>(requestBody),
    },
    [VENDOR_COMMANDS.CARVANA_CONTACT_FOLLOW_UP_NAME_SHOW]: {
      route: 'contact-follow-up-name-show',
      payload: mapProperties<ContactFollowUpNameShowRequestBody>(requestBody),
    },
    [VENDOR_COMMANDS.CARVANA_CONTACT_FOLLOW_UP_QUESTION]: {
      route: 'contact-follow-up-question',
      payload: mapProperties<ContactFollowUpQuestionRequestBody>(requestBody),
    },
    [VENDOR_COMMANDS.CARVANA_CONTACT_FOLLOWUP_VERIFICATION_RESEND]: {
      route: 'contact-follow-up-verification-resend',
      payload: mapProperties<ContactFollowUpVerificationResendRequestBody>(requestBody),
    },
    [VENDOR_COMMANDS.CARVANA_RATING_CONTACT_FOLLOWUP_VERIFICATION]: {
      route: 'contact-follow-up-verification',
      payload: mapProperties<ContactFollowUpVerificationRequestBody>(requestBody),
    },
    [VENDOR_COMMANDS.CARVANA_CUSTOMER_CHAT_RATING]: {
      route: 'customer-chat-rating',
      payload: mapProperties<CustomerChatRatingRequestBody>(requestBody),
    },
    [VENDOR_COMMANDS.CARVANA_CONTACT_FOLLOWUP_NAME]: {
      route: 'contact-follow-up-name',
      payload: mapProperties<ContactFollowUpNameRequestBody>(requestBody),
    },
    [VENDOR_COMMANDS.CARVANA_AI_SEND_MESSSAGE]: {
      route: 'send-ai-message',
      payload: mapProperties<SendAiMessageRequestBody>(requestBody),
    },
  };

  const _command = commands[command];

  const url = `${baseUrl}/${_command.route}`;
  return axiosWithRetry({
    url,
    headers: getHeaders(accessToken),
    body: _command.payload,
    method: HTTP_REQUEST_METHODS.POST,
  });
};
